import type {
  AICheckRunResult,
  FailedCheckInfo,
  FailedStage,
} from "@/types/aicheck";

export interface RunFailureSummary {
  failedStage?: FailedStage;
  failedChecks?: FailedCheckInfo[];
  stageError?: string;
}

function chooseRicherError(a: string, b: string): string {
  const score = (s: string) =>
    s.length +
    (s.includes("request_id=") ? 40 : 0) +
    (s.includes("details=") ? 40 : 0) +
    (s.includes("type=") ? 20 : 0) +
    (s.includes("code=") ? 20 : 0);
  return score(b) > score(a) ? b : a;
}

function extractOpenAIStatusCode(message: string): number | null {
  const m = message.match(/OpenAI API error:? ?(\d{3})/i);
  if (!m) return null;
  const code = Number(m[1]);
  return Number.isFinite(code) ? code : null;
}

function hasRichOpenAIDetail(message: string): boolean {
  return (
    message.includes("request_id=") ||
    message.includes("details=") ||
    message.includes("response_body=")
  );
}

export function deriveRunFailureSummary(
  result: Pick<
    AICheckRunResult,
    "aiResults" | "customerProfileResult" | "contactTopicsResult"
  >,
): RunFailureSummary {
  const failedNodeMap = new Map<FailedCheckInfo["checkId"], FailedCheckInfo>();
  for (const r of result.aiResults) {
    if (r.status !== "error") continue;
    const nodeId =
      r.batchGroupId ??
      (r.batchGroupName ? `batch-name:${r.batchGroupName}` : r.checkId);
    const existing = failedNodeMap.get(nodeId);
    if (existing) {
      const mergedRerunIds = [
        ...new Set([...(existing.rerunCheckIds ?? []), r.checkId]),
      ];
      failedNodeMap.set(nodeId, {
        ...existing,
        rerunCheckIds: mergedRerunIds,
        error: chooseRicherError(existing.error, r.error ?? "Unknown"),
      });
      continue;
    }
    failedNodeMap.set(nodeId, {
      checkId: nodeId,
      checkName: r.batchGroupName ?? r.checkName,
      batchGroupId: r.batchGroupId,
      batchGroupName: r.batchGroupName,
      rerunCheckIds: [r.checkId],
      error: r.error ?? "Unknown",
    });
  }
  const failedChecks = [...failedNodeMap.values()];
  const richStageErrorCandidate =
    result.contactTopicsResult?.error ?? result.customerProfileResult?.error;
  if (richStageErrorCandidate && hasRichOpenAIDetail(richStageErrorCandidate)) {
    const stageCode = extractOpenAIStatusCode(richStageErrorCandidate);
    if (stageCode != null) {
      for (const fc of failedChecks) {
        const checkCode = extractOpenAIStatusCode(fc.error);
        if (checkCode != null && checkCode === stageCode) {
          fc.error = chooseRicherError(fc.error, richStageErrorCandidate);
        }
      }
    }
  }

  let failedStage: FailedStage | undefined;
  if (
    result.contactTopicsResult?.error ||
    result.contactTopicsResult?.compactionDegraded
  ) {
    failedStage = "compaction";
  } else if (
    result.customerProfileResult?.error &&
    !result.customerProfileResult.profile
  ) {
    failedStage = "profile";
  } else if (failedChecks.length > 0) {
    failedStage = "ai-checks";
  }

  const stageError =
    failedStage === "compaction"
      ? (result.contactTopicsResult?.error ?? "Compaction degraded")
      : failedStage === "profile"
        ? result.customerProfileResult?.error
        : failedStage === "ai-checks" && failedChecks.length > 0
          ? failedChecks[0]?.error
          : undefined;

  return {
    failedChecks: failedChecks.length > 0 ? failedChecks : undefined,
    failedStage,
    stageError,
  };
}
